import moment from 'moment';
import {
  generatePdfFromTemplate,
  PdfTemplateLayoutItem,
  GeneratePdfTemplateResult,
  PdfTemplateFontWeight,
} from './PdfTemplateService';

const DISCIPLINARY_ACTION_PDF_URL =
  'https://arb-systems-s3-public.s3.amazonaws.com/public/Performance/PerformanceManagement_DisciplinaryAction.pdf';

const formatDateValue = (value?: string) => {
  if (!value) {
    return '';
  }

  const parsed = moment(value);
  if (!parsed.isValid()) {
    return value;
  }

  return parsed.format('MM/DD/YYYY');
};

const extractField = (data: any, candidates: string[]) => {
  for (const key of candidates) {
    const segments = key.split('.');
    let current = data;

    for (const segment of segments) {
      if (current && Object.prototype.hasOwnProperty.call(current, segment)) {
        current = current[segment];
      } else {
        current = undefined;
        break;
      }
    }

    if (current !== undefined && current !== null) {
      if (typeof current === 'string') {
        const trimmed = current.trim();
        if (trimmed !== '') {
          return trimmed;
        }
      }

      if (typeof current === 'number') {
        return current.toString();
      }

      if (current instanceof Date) {
        return moment(current).format('MM/DD/YYYY');
      }
    }
  }

  return '';
};

const getPolicyViolationText = (item: any): string => {
  if (item?.policyViolations && Array.isArray(item.policyViolations) && item.policyViolations.length > 0) {
    return item.policyViolations
      .map((pv: any) => (typeof pv === 'string' ? pv : pv?.name ?? ''))
      .filter(Boolean)
      .join(', ');
  }
  return extractField(item, ['policy_violation', 'policyViolation', 'policyViolation.name', 'policy_violated']) || '';
};

const sanitizeFileName = (text: string) => {
  if (!text) {
    return 'disciplinary-action';
  }

  return (
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 80) || 'disciplinary-action'
  );
};

interface AddTextOptions {
  y?: number;
  x?: number;
  textColor?: string;
  page?: number;
  fontSize?: number;
  fontWeight?: PdfTemplateFontWeight;
  maxWidth?: number;
  lineHeight?: number;
  spacingAfter?: number;
}

const buildDisciplinaryActionLayout = (item: any, userLogged?: any, currentSchool?: any) => {
  const employeeName = extractField(item, ['name', 'employeeName', 'employee_name', 'employee.name']) || '';
  const position = extractField(item, ['position', 'employeePosition', 'jobTitle', 'job_title']) || '';
  const department = extractField(item, ['department', 'department.name', 'departmentName']) || '';
  const supervisor = extractField(item, ['supervisor', 'supervisor.name', 'supervisorName', 'supervisor_name']) || '';
  const dateOfIncident = formatDateValue(extractField(item, ['date', 'dateOfIncident', 'date_of_incident']));
  const dateOfHire = formatDateValue(extractField(item, ['date_of_hire', 'dateOfHire']));
  const policyViolation = getPolicyViolationText(item);
  const reasonForDisciplinaryAction = extractField(item, [
    'reason_for_disciplinary_action',
    'reasonForDisciplinaryAction',
    'reason',
  ]);
  const actionTaken = extractField(item, ['action_taken', 'actionTaken', 'action']);
  const expectedImprovement = extractField(item, [
    'expectationOfImprovement',
    'expected_improvement',
    'expectedImprovement',
    'expectations',
  ]);
  const createdAt = formatDateValue(extractField(item, ['created_at', 'createdAt']));
  const followUpMeetingDate = formatDateValue(
    extractField(item, ['meetingDate', 'meeting_date', 'followUpMeetingDate', 'follow_up_meeting_date']),
  );
  const _programName = extractField(item, ['programName', 'program_name']);

  const layout: PdfTemplateLayoutItem[] = [];
  const addText = (text: string, options: AddTextOptions = {}) => {
    const { x, ...rest } = options;
    const textItem: PdfTemplateLayoutItem = {
      type: 'text',
      text,
      x: x ?? 54,
      ...rest,
    };
    layout.push(textItem);
  };

  const addSpacing = (height: number) => {
    layout.push({ type: 'spacing', height });
  };

  addText(`${currentSchool?.label ?? ''}`, {
    fontWeight: 'regular',
    fontSize: 16,
    textColor: '#fd0017',
    y: 711,
    x: 203,
  });

  const submittedByName = item?.submittedByUser
    ? `${item.submittedByUser.firstName ?? ''} ${item.submittedByUser.lastName ?? ''}`.trim()
    : '';

  addText(submittedByName, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 678,
    x: 150,
  });

  addText(`${formatDateValue(extractField(item, ['created_at', 'createdAt']))}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 661,
    x: 157,
  });

  addText(`${employeeName}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 626,
    x: 110,
  });

  addText(`${dateOfIncident}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 626,
    x: 400,
  });

  addText(`${position}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 600,
    x: 120,
  });

  addText(`${department ?? ''}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 600,
    x: 360,
  });

  const sequentialX = 80;
  const sequentialFontSize = 12;
  const spacingAfterTitle = 10;
  const spacingAfterContent = 22;

  addSpacing(35);

  addText('Action Taken:', {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterTitle,
  });
  addText(`${actionTaken ?? ''}`, {
    fontWeight: 'regular',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterContent,
  });

  addText('Policy Violation:', {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterTitle,
  });
  addText(`${policyViolation ?? ''}`, {
    fontWeight: 'regular',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterContent,
  });

  addText('Reason for Disciplinary Action:', {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterTitle,
  });
  addText(`${reasonForDisciplinaryAction ?? ''}`, {
    fontWeight: 'regular',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterContent,
  });

  addText('Expected Improvement:', {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterTitle,
  });
  addText(`${expectedImprovement ?? ''}`, {
    fontWeight: 'regular',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterContent,
  });

  addSpacing(20);

  const followUpText = followUpMeetingDate
    ? `We'll have a follow up meeting on ${followUpMeetingDate}`
    : `We'll have a follow up meeting on`;
  addText(followUpText, {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    textColor: '#ff0000',
    spacingAfter: 12,
  });

  return {
    layout,
    employeeName,
    helpers: {
      addText,
      extractField,
      formatDateValue,
    },
    context: {
      item,
      userLogged,
      currentSchool,
      position,
      department,
      supervisor,
      dateOfIncident,
      dateOfHire,
      policyViolation,
      reasonForDisciplinaryAction,
      actionTaken,
      expectedImprovement,
      createdAt,
      followUpMeetingDate,
    },
  };
};

export interface DisciplinaryActionPdfResult extends GeneratePdfTemplateResult {
  fileName: string;
}

export const generateDisciplinaryActionPdf = async (
  item: any,
  userLogged?: any,
  currentSchool?: any,
): Promise<DisciplinaryActionPdfResult> => {
  const { layout, employeeName } = buildDisciplinaryActionLayout(item, userLogged, currentSchool);
  const result = await generatePdfFromTemplate({
    templatePath: DISCIPLINARY_ACTION_PDF_URL,
    layout,
    cursorStart: 730,
    templateLastPageIndex: 1,
  });

  return {
    ...result,
    fileName: `${sanitizeFileName(employeeName)}-disciplinary-action.pdf`,
  };
};
